import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import { DocumentChartBarIcon, UserGroupIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const BookingsReport = ({ data, dateRange }) => {
  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Make sure data exists and has the expected structure
  // Handle both API response format and placeholder data format
  const reportData = data?.data || data || {};
  
  // Ensure daily_bookings is an array
  const dailyBookingsArray = Array.isArray(reportData.daily_bookings) ? reportData.daily_bookings : [];
  
  // Daily bookings chart data
  const dailyBookingsData = {
    labels: dailyBookingsArray.map(item => formatDate(item.date || new Date())),
    datasets: [
      {
        label: 'Daily Bookings',
        data: dailyBookingsArray.map(item => item.count || 0),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Ensure bookings_by_status is an array
  const statusArray = Array.isArray(reportData.bookings_by_status) ? reportData.bookings_by_status : [];
  
  // Bookings by status chart data
  const bookingsByStatusData = {
    labels: statusArray.map(item => item.status || 'Unknown'),
    datasets: [
      {
        label: 'Bookings by Status',
        data: statusArray.map(item => item.count || 0),
        backgroundColor: [
          'rgba(16, 185, 129, 0.7)', // Confirmed - Green
          'rgba(245, 158, 11, 0.7)', // Pending - Yellow
          'rgba(239, 68, 68, 0.7)',  // Cancelled - Red
        ],
        borderWidth: 1,
      },
    ],
  };

  // Ensure bookings_by_tour is an array
  const tourArray = Array.isArray(reportData.bookings_by_tour) ? reportData.bookings_by_tour : [];
  
  // Bookings by tour chart data
  const bookingsByTourData = {
    labels: tourArray.map(item => item.tour_name || 'Unknown Tour'),
    datasets: [
      {
        label: 'Bookings by Tour',
        data: tourArray.map(item => item.count || 0),
        backgroundColor: [
          'rgba(79, 70, 229, 0.7)',
          'rgba(59, 130, 246, 0.7)',
          'rgba(16, 185, 129, 0.7)',
          'rgba(245, 158, 11, 0.7)',
          'rgba(239, 68, 68, 0.7)',
        ],
        borderWidth: 1,
      },
    ],
  };

  // Chart options
  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Daily Bookings Trend',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          precision: 0
        }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Bookings by Status',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((acc, curr) => acc + curr, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Bookings by Tour',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          precision: 0
        }
      }
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Bookings Report</h2>
        <p className="text-sm text-gray-500 mb-6">
          Booking data from {formatDate(dateRange.startDate)} to {formatDate(dateRange.endDate)}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentChartBarIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Bookings</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{reportData.total_bookings || 0}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Confirmed Bookings</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{reportData.confirmed_bookings || 0}</div>
                    <div className="text-sm text-gray-500">
                      {reportData.total_bookings ? Math.round(((reportData.confirmed_bookings || 0) / reportData.total_bookings) * 100) : 0}% of total
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentChartBarIcon className="h-6 w-6 text-yellow-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Pending Bookings</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{reportData.pending_bookings || 0}</div>
                    <div className="text-sm text-gray-500">
                      {reportData.total_bookings ? Math.round(((reportData.pending_bookings || 0) / reportData.total_bookings) * 100) : 0}% of total
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserGroupIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Average Group Size</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{reportData.average_group_size || 0}</div>
                    <div className="text-sm text-gray-500">Travelers per booking</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="space-y-8">
        {/* Daily Bookings Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Daily Bookings Trend</h3>
          <div className="h-80">
            <Line data={dailyBookingsData} options={lineChartOptions} />
          </div>
        </div>

        {/* Bookings by Status and Tour Charts */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Bookings by Status</h3>
            <div className="h-80 flex items-center justify-center">
              <Pie data={bookingsByStatusData} options={pieChartOptions} />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Bookings by Tour</h3>
            <div className="h-80">
              <Bar data={bookingsByTourData} options={barChartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Bookings by Tour Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Bookings by Tour</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tour Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bookings
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % of Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(tourArray) ? tourArray.map((tour, index) => {
                  const percentage = reportData.total_bookings ? ((tour.count || 0) / reportData.total_bookings) * 100 : 0;
                  return (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {tour.tour_name || 'Unknown Tour'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {tour.count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {percentage.toFixed(1)}%
                      </td>
                    </tr>
                  );
                }) : <tr><td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">No tour data available</td></tr>}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    Total
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                    {reportData.total_bookings || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                    100%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bookings by Status Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Bookings by Status</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bookings
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % of Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(statusArray) ? statusArray.map((status, index) => {
                  const percentage = reportData.total_bookings ? ((status.count || 0) / reportData.total_bookings) * 100 : 0;
                  let statusColor = 'text-gray-900';
                  
                  if ((status.status || '').toLowerCase() === 'confirmed') {
                    statusColor = 'text-green-600';
                  } else if ((status.status || '').toLowerCase() === 'pending') {
                    statusColor = 'text-yellow-600';
                  } else if ((status.status || '').toLowerCase() === 'cancelled') {
                    statusColor = 'text-red-600';
                  }
                  
                  return (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${statusColor}`}>
                        {status.status || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {status.count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        {percentage.toFixed(1)}%
                      </td>
                    </tr>
                  );
                }) : <tr><td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">No status data available</td></tr>}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    Total
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                    {reportData.total_bookings || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                    100%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingsReport;
